import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { Save } from "lucide-react";
import { updatePatientBasicInfo } from "../../networkCalls";
import { convertErrorResponseToMessage, showToastType } from "../../utils";
import { patientBasicInfoType } from "./PatientTypes";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { Skeleton } from "../../../components/ui/skeleton";

// Kept for backwards-compat: networkCalls.ts imports this as the payload type
// for updatePatientBasicInfo. The save payload shape is unchanged.
export type FormValuesTypes = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  refferedBy: string | null;
};

const GeneralInfoSchema = z.object({
  firstName: z.string().trim().min(1, { message: "First name is required" }),
  lastName: z.string().trim().optional(),
  email: z
    .string()
    .trim()
    .email({ message: "Enter a valid email address" })
    .optional()
    .or(z.literal("")),
  phoneNumber: z.string().trim().optional(),
  refferedBy: z.string().trim().optional(),
});

type GeneralInfoValues = z.infer<typeof GeneralInfoSchema>;

function toFormValues(state: patientBasicInfoType): GeneralInfoValues {
  return {
    firstName: state.firstName ?? "",
    lastName: state.lastName ?? "",
    email: state.email ?? "",
    phoneNumber: state.phoneNumber ?? "",
    refferedBy: state.refferedBy ?? "",
  };
}

export function PatientProfileForm({
  patientId,
  patientBasicInfoState,
  setPatientBasicInfoState,
}: {
  patientId: string;
  patientBasicInfoState: patientBasicInfoType;
  setPatientBasicInfoState: any;
}) {
  const form = useForm<GeneralInfoValues>({
    resolver: zodResolver(GeneralInfoSchema),
    mode: "onChange",
    defaultValues: toFormValues(patientBasicInfoState),
  });

  // Keep the form in sync as the patient loads / updates (e.g. after a save the
  // parent state changes, which also resets the dirty flag here).
  useEffect(() => {
    form.reset(toFormValues(patientBasicInfoState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientBasicInfoState]);

  const {
    formState: { isDirty, isValid, isSubmitting },
  } = form;

  function onSubmit(values: GeneralInfoValues) {
    if (patientBasicInfoState.id == null) {
      return;
    }
    // Normalize blank/whitespace fields to null; payload shape matches the API.
    const payload: FormValuesTypes = {
      firstName: values.firstName?.trim() || null,
      lastName: values.lastName?.trim() || null,
      email: values.email?.trim() || null,
      phoneNumber: values.phoneNumber?.trim() || null,
      refferedBy: values.refferedBy?.trim() || null,
    };

    updatePatientBasicInfo(patientBasicInfoState.id, payload)
      .then((updatedPatientInfo) => {
        // The PATCH response can come back empty/200-no-body. Since the server
        // accepted the update, fall back to the values we just saved so the
        // header and inputs stay populated (and keep the existing id).
        const saved =
          updatedPatientInfo && updatedPatientInfo.id
            ? updatedPatientInfo
            : null;
        setPatientBasicInfoState({
          id: saved?.id ?? patientBasicInfoState.id,
          firstName: saved?.firstName ?? payload.firstName,
          lastName: saved?.lastName ?? payload.lastName,
          email: saved?.email ?? payload.email,
          phoneNumber: saved?.phoneNumber ?? payload.phoneNumber,
          refferedBy: saved?.refferedBy ?? payload.refferedBy,
        });
        toast.success("Update Patient Basic Info Successfully");
      })
      .catch((error) => {
        console.error(error.message);
        if (error instanceof AxiosError) {
          const addPatientErrorObject = convertErrorResponseToMessage(error);
          const message = addPatientErrorObject["message"];
          const status = addPatientErrorObject["statusCode"];
          showToastType(message, status);
        } else if (
          error.hasOwnProperty("response") &&
          error.response.hasOwnProperty("data") &&
          error.response.data.hasOwnProperty("message")
        ) {
          var errorMessage = error.message;
          errorMessage = error.response.data.message;
          toast.error(errorMessage);
        } else {
          toast.error("Unexpected Error occured. Please try again");
        }
      });
  }

  if (patientBasicInfoState.id === null) {
    return (
      <Card className="w-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>General Information</CardTitle>
        <CardDescription>
          Patient name, contact details, and referral.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Name */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">Name</h3>
                <Separator className="mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            {/* Contact */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">Contact</h3>
                <Separator className="mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="name@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Phone number"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            {/* Referral */}
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">Referral</h3>
                <Separator className="mt-2" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="refferedBy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Referred By</FormLabel>
                      <FormControl>
                        <Input placeholder="Referring doctor / source" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!isDirty || !isValid || isSubmitting}
              >
                <Save className="h-4 w-4" />
                Save Patient Profile
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
